import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  migrationService,
  DEFAULT_MIGRATION_REQUEST,
  type MigrationRunRequest,
  type MigrationRunResponse,
} from '@kss/api';
// `v` is aliased to `val` here: the local `set` helper below takes a
// parameter named `v`, which would shadow the import inside it.
import { Card, Button, Input, useToast, v as val } from '@kss/ui';

/**
 * Provisions a new tenant catalog by POSTing the connection-parameter
 * body documented in BACKEND_AGENT_PROMPTS.md (M1) to
 * `/api/Migration/Run`. The same call seeds menus + roles +
 * permissions + bootstrap admin (M2) and returns counts so the user
 * can sanity-check the run.
 *
 * Leave `tenantId` blank to let the server generate a fresh GUID.
 */
export default function MigrateTenant() {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState<MigrationRunRequest>(DEFAULT_MIGRATION_REQUEST);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MigrationRunResponse | null>(null);

  const set = <K extends keyof MigrationRunRequest>(k: K, v: MigrationRunRequest[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  /* Field errors. The two presence checks below were already here; what
     was missing was any bound on the values — an unbounded `dataSource`
     or a catalog name carrying a semicolon goes straight into a
     connection string, where `;` is the separator. That makes the
     injection guard load-bearing here rather than defence-in-depth. */
  const migrateErrors: Record<string, string | null> = {
    dataSource: val.runRules(form.dataSource, [
      val.required('Data source'),
      val.maxLength(200, 'Data source'),
      ...val.safeText('Data source'),
    ]),
    initialCatalog: val.runRules(form.initialCatalog, [
      val.required('Initial catalog'),
      val.maxLength(128, 'Initial catalog'),
      ...val.safeText('Initial catalog'),
    ]),
    applicationName: val.runRules(form.applicationName ?? '', [
      val.maxLength(128, 'Application name'),
      ...val.safeText('Application name'),
    ]),
    // Only required under SQL auth; integrated security uses the
    // service account and leaves both blank.
    userId: form.integratedSecurity
      ? null
      : val.runRules(form.userId, [val.required('User id'), val.maxLength(128, 'User id')]),
    password: form.integratedSecurity
      ? null
      : val.runRules(form.password, [val.required('Password')]),
  };
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const migErr = (k: string) => (touched[k] ? migrateErrors[k] ?? undefined : undefined);
  const markM = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const migrateValid = !Object.values(migrateErrors).some(Boolean);

  const handleRun = async () => {
    setTouched({
      dataSource: true,
      initialCatalog: true,
      applicationName: true,
      userId: true,
      password: true,
    });
    const problem = Object.values(migrateErrors).find(Boolean);
    if (problem) {
      toast.error('Fix errors', problem);
      return;
    }
    setBusy(true);
    try {
      const res = await migrationService.run({
        ...form,
        tenantId: form.tenantId?.trim() ? form.tenantId : null,
      });
      setResult(res);
      toast.success(
        'Migration completed',
        `Seeded ${res.menuCount} menus, ${res.permissionCount} permissions, ${res.roleCount} roles. Admin user: ${res.adminUserId}.`
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Migration failed';
      toast.error('Migration failed', msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4">
      <Card className="p-4">
        <header className="mb-4 flex items-center justify-between gap-2">
          <div>
            <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Migrate tenant
            </h1>
            <p className="text-xs text-[var(--color-text-secondary)]">
              Provisions the database schema and seeds menus, roles, permissions
              and the bootstrap admin user (`admin / Admin@123`).
            </p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/panel/tenants')}>
            Back
          </Button>
        </header>

        <div className="grid gap-3 md:grid-cols-2">
          <Input
            label="Tenant ID (leave blank to auto-generate)"
            placeholder="3fa85f64-5717-4562-b3fc-2c963f66afa6"
            value={form.tenantId ?? ''}
            onChange={(e) => set('tenantId', e.target.value)}
          />
          <Input
            label="Application name"
            value={form.applicationName}
            onChange={(e) => set('applicationName', e.target.value)}
            onBlur={markM("applicationName")}
            error={migErr("applicationName")}
          />
          <Input
            label="Data source (server)"
            placeholder="103.173.204.104"
            required
            value={form.dataSource}
            onChange={(e) => set('dataSource', e.target.value)}
            onBlur={markM("dataSource")}
            error={migErr("dataSource")}
          />
          <Input
            label="Initial catalog (database)"
            placeholder="KSS_Mart"
            required
            value={form.initialCatalog}
            onChange={(e) => set('initialCatalog', e.target.value)}
            onBlur={markM("initialCatalog")}
            error={migErr("initialCatalog")}
          />
          <Input
            label="User id"
            placeholder="kristy"
            disabled={form.integratedSecurity}
            value={form.userId}
            onChange={(e) => set('userId', e.target.value)}
            onBlur={markM("userId")}
            error={migErr("userId")}
          />
          <Input
            label="Password"
            type="password"
            disabled={form.integratedSecurity}
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            onBlur={markM("password")}
            error={migErr("password")}
          />
          <NumberField label="Min pool size" value={form.minPoolSize} onChange={(v) => set('minPoolSize', v)} />
          <NumberField label="Max pool size" value={form.maxPoolSize} onChange={(v) => set('maxPoolSize', v)} />
          <NumberField label="Connect timeout (s, 0 = infinite)" value={form.connectTimeout} onChange={(v) => set('connectTimeout', v)} />
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <ToggleRow label="Integrated security (Windows auth)" value={form.integratedSecurity} onChange={(v) => set('integratedSecurity', v)} />
          <ToggleRow label="Trust server certificate" value={form.trustServerCertificate} onChange={(v) => set('trustServerCertificate', v)} />
          <ToggleRow label="Encrypt connection" value={form.encrypt} onChange={(v) => set('encrypt', v)} />
        </div>

        <footer className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => setForm(DEFAULT_MIGRATION_REQUEST)} disabled={busy}>
            Reset
          </Button>
          <Button onClick={handleRun} loading={busy} disabled={busy || !migrateValid}>
            Run migration
          </Button>
        </footer>
      </Card>

      {result ? (
        <Card className="p-4">
          <h2 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
            Last run
          </h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
            <Stat label="Tenant ID" value={result.tenantId} />
            <Stat label="Admin user ID" value={result.adminUserId} />
            <Stat label="Menus seeded" value={String(result.menuCount)} />
            <Stat label="Permissions" value={String(result.permissionCount)} />
            <Stat label="Roles" value={String(result.roleCount)} />
          </dl>
          {result.message ? (
            <p className="mt-2 text-xs text-[var(--color-text-secondary)]">{result.message}</p>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Input
      label={label}
      type="number"
      value={String(value)}
      onChange={(e) => {
        const n = Number(e.target.value);
        onChange(Number.isFinite(n) ? n : 0);
      }}
    />
  );
}

function ToggleRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2 text-xs text-[var(--color-text-primary)]">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[var(--color-text-muted)]">{label}</dt>
      <dd className="break-all font-mono text-[var(--color-text-primary)]">{value}</dd>
    </div>
  );
}
